import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class SsOneDoneRotation extends HTMLElement {
  /** @type {Record<string, RotationProduct | null>} */
  #products = {};

  /** @type {WeakMap<HTMLElement, { productKey: string, product: RotationProduct, variant: RotationVariant }>} */
  #rowState = new WeakMap();

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {HTMLButtonElement | null} */
  #submitButton = null;

  /** @type {HTMLElement | null} */
  #buttonLabel = null;

  /** @type {HTMLElement | null} */
  #status = null;

  /** @type {HTMLElement[]} */
  #rows = [];

  connectedCallback() {
    if (this.dataset.initialized === 'true' && this.#form) {
      this.#form.addEventListener('change', this.#handleChange);
      this.#form.addEventListener('submit', this.#handleSubmit);
      return;
    }

    this.#form = this.querySelector('[data-rotation-form]');
    this.#submitButton = this.querySelector('[data-rotation-submit]');
    this.#buttonLabel = this.querySelector('[data-rotation-button-label]');
    this.#status = this.querySelector('[data-rotation-status]');
    this.#rows = Array.from(this.querySelectorAll('[data-rotation-row]'));
    this.#products = this.#readProducts();

    if (!this.#form || !this.#submitButton || !this.#status || this.#rows.length === 0) return;

    this.#form.addEventListener('change', this.#handleChange);
    this.#form.addEventListener('submit', this.#handleSubmit);

    for (const row of this.#rows) {
      this.#initializeRow(row);
    }

    this.dataset.initialized = 'true';
    this.dataset.ready = 'true';
    this.#form.hidden = false;
    this.#updateSummary();
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('change', this.#handleChange);
    this.#form?.removeEventListener('submit', this.#handleSubmit);
  }

  /** @returns {Record<string, RotationProduct | null>} */
  #readProducts() {
    const source = this.querySelector('[data-rotation-products]');
    if (!source?.textContent) return {};

    try {
      const parsed = JSON.parse(source.textContent);
      return Object.fromEntries(
        Object.entries(parsed).map(([key, product]) => [key, this.#normalizeProduct(product)])
      );
    } catch (error) {
      console.error('Unable to read One & Done rotation products.', error);
      return {};
    }
  }

  /**
   * @param {unknown} source
   * @returns {RotationProduct | null}
   */
  #normalizeProduct(source) {
    if (!source || typeof source !== 'object') return null;

    const product = /** @type {Record<string, unknown>} */ (source);
    const optionNames = Array.isArray(product.options) ? product.options.map(String) : [];
    const variants = Array.isArray(product.variants)
      ? product.variants
          .filter((variant) => variant && typeof variant === 'object')
          .map((variant) => {
            const value = /** @type {Record<string, unknown>} */ (variant);
            return {
              id: Number(value.id),
              available: value.available === true,
              price: Number(value.price),
              options: Array.isArray(value.options) ? value.options.map(String) : [],
            };
          })
          .filter(
            (variant) =>
              Number.isSafeInteger(variant.id) &&
              Number.isFinite(variant.price) &&
              variant.options.length === optionNames.length
          )
      : [];

    if (optionNames.length === 0 || variants.length === 0) return null;

    return {
      id: Number(product.id),
      title: String(product.title || ''),
      url: String(product.url || ''),
      options: optionNames,
      variants,
    };
  }

  /** @param {HTMLElement} row */
  #initializeRow(row) {
    const styleSelect = /** @type {HTMLSelectElement | null} */ (row.querySelector('[data-rotation-style]'));
    if (!styleSelect) return;

    for (const option of styleSelect.options) {
      option.disabled = !this.#productIsAvailable(this.#products[option.value]);
    }

    const selectedProduct = this.#products[styleSelect.value];
    if (this.#productIsAvailable(selectedProduct)) {
      this.#resetRow(row, styleSelect.value);
      return;
    }

    const firstAvailableStyle = Array.from(styleSelect.options).find((option) => !option.disabled);
    if (firstAvailableStyle) {
      styleSelect.value = firstAvailableStyle.value;
      this.#resetRow(row, firstAvailableStyle.value);
      return;
    }

    styleSelect.disabled = true;
    this.#setRowUnavailable(row);
  }

  /**
   * Product changes always start from that product's own first available
   * variant. No color or size value is carried across products.
   * @param {HTMLElement} row
   * @param {string} productKey
   */
  #resetRow(row, productKey) {
    const product = this.#products[productKey];
    if (!this.#productIsAvailable(product)) {
      this.#rowState.delete(row);
      this.#setRowUnavailable(row);
      return;
    }

    const firstAvailableVariant = product.variants.find((variant) => variant.available);
    if (!firstAvailableVariant) {
      this.#rowState.delete(row);
      this.#setRowUnavailable(row);
      return;
    }

    this.#rowState.set(row, {
      productKey,
      product,
      variant: firstAvailableVariant,
    });
    this.#renderOptionSelectors(row, product, firstAvailableVariant);
    this.#syncOptionAvailability(row);
    this.#renderRow(row);
  }

  /**
   * @param {HTMLElement} row
   * @param {RotationProduct} product
   * @param {RotationVariant} variant
   */
  #renderOptionSelectors(row, product, variant) {
    const container = row.querySelector('[data-rotation-options]');
    if (!container) return;

    container.replaceChildren();
    const rowIndex = row.dataset.rowIndex || '1';

    product.options.forEach((optionName, optionIndex) => {
      const field = document.createElement('div');
      field.className = 'ss-ood-rotation__field';

      const label = document.createElement('label');
      const select = document.createElement('select');
      const selectId = `${this.id}-row-${rowIndex}-option-${optionIndex}`;

      label.htmlFor = selectId;
      label.textContent = this.#displayOptionName(optionName);

      select.id = selectId;
      select.name = `rotation_${rowIndex}_option_${optionIndex}`;
      select.required = true;
      select.dataset.rotationOption = '';
      select.dataset.optionIndex = String(optionIndex);

      const values = [
        ...new Set(product.variants.map((candidate) => candidate.options[optionIndex]).filter(Boolean)),
      ];
      for (const value of values) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        option.selected = value === variant.options[optionIndex];
        select.append(option);
      }

      field.append(label, select);
      container.append(field);
    });
  }

  /** @param {string} optionName */
  #displayOptionName(optionName) {
    const normalized = optionName.trim().toLocaleLowerCase();
    if (normalized === 'color' || normalized === 'colour') return this.dataset.optionColorLabel || optionName;
    if (normalized === 'size' || normalized === 'talla') return this.dataset.optionSizeLabel || optionName;
    return optionName;
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;

    const row = /** @type {HTMLElement | null} */ (target.closest('[data-rotation-row]'));
    if (!row) return;

    this.#clearStatus();

    if (target.matches('[data-rotation-style]')) {
      this.#resetRow(row, target.value);
      this.#updateSummary();
      return;
    }

    if (!target.matches('[data-rotation-option]')) return;

    const state = this.#rowState.get(row);
    if (!state) return;

    const selects = this.#getOptionSelects(row);
    const selectedValues = selects.map((select) => select.value);
    const changedIndex = Number(target.dataset.optionIndex);
    let selectedVariant = this.#findExactVariant(state.product, selectedValues);

    if (!selectedVariant) {
      selectedVariant =
        state.product.variants.find(
          (variant) =>
            variant.available &&
            selectedValues
              .slice(0, changedIndex + 1)
              .every((value, index) => variant.options[index] === value)
        ) ||
        state.product.variants.find(
          (variant) => variant.available && variant.options[changedIndex] === target.value
        ) ||
        state.product.variants.find((variant) => variant.available);
    }

    if (!selectedVariant) {
      this.#rowState.delete(row);
      this.#setRowUnavailable(row);
      this.#updateSummary();
      return;
    }

    for (const [index, select] of selects.entries()) {
      select.value = selectedVariant.options[index];
    }

    state.variant = selectedVariant;
    this.#rowState.set(row, state);
    this.#syncOptionAvailability(row);
    this.#renderRow(row);
    this.#updateSummary();
  };

  /**
   * Each option value is enabled only when at least one available variant
   * matches the choices before it. This keeps every reachable selection on a
   * real path through the product's availability matrix.
   * @param {HTMLElement} row
   */
  #syncOptionAvailability(row) {
    const state = this.#rowState.get(row);
    if (!state) return;

    const selects = this.#getOptionSelects(row);
    const selectedValues = selects.map((select) => select.value);

    for (const [optionIndex, select] of selects.entries()) {
      for (const option of select.options) {
        option.disabled = !state.product.variants.some(
          (variant) =>
            variant.available &&
            variant.options[optionIndex] === option.value &&
            selectedValues
              .slice(0, optionIndex)
              .every((value, precedingIndex) => variant.options[precedingIndex] === value)
        );
      }
    }
  }

  /** @param {HTMLElement} row */
  #renderRow(row) {
    const state = this.#rowState.get(row);
    const price = row.querySelector('[data-rotation-row-price]');
    const message = row.querySelector('[data-rotation-row-message]');
    if (!state || !price) return;

    row.dataset.variantId = String(state.variant.id);
    price.textContent = this.#formatPrice(state.variant.price);
    if (message) {
      message.textContent = '';
      message.hidden = true;
    }
    for (const select of row.querySelectorAll('select')) {
      select.removeAttribute('aria-invalid');
    }
  }

  /** @param {HTMLElement} row */
  #setRowUnavailable(row) {
    row.removeAttribute('data-variant-id');
    row.querySelector('[data-rotation-options]')?.replaceChildren();
    const price = row.querySelector('[data-rotation-row-price]');
    const message = row.querySelector('[data-rotation-row-message]');
    if (price) price.textContent = '';
    if (message) {
      message.textContent = this.dataset.unavailable || '';
      message.hidden = false;
    }
  }

  /** @param {Event} event */
  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.dataset.state === 'loading') return;

    const selections = this.#rows.map((row) => this.#rowState.get(row)).filter(Boolean);
    if (selections.length !== this.#rows.length) {
      this.#showValidationError();
      return;
    }

    /** @type {Map<number, number>} */
    const quantitiesByVariant = new Map();
    for (const selection of selections) {
      const variantId = selection.variant.id;
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + 1);
    }

    const items = Array.from(quantitiesByVariant, ([id, quantity]) => ({ id, quantity }));
    const sectionIds = [
      ...new Set(
        Array.from(document.querySelectorAll('cart-items-component'))
          .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
          .filter(Boolean)
      ),
    ];
    const payload = {
      items,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    this.#setLoading(true);
    this.#setStatus(this.dataset.addingLabel || '', 'loading');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok || data.status || data.errors) {
        const message = data.description || data.message || this.dataset.errorFallback || '';
        this.dispatchEvent(new CartErrorEvent(this.id, message, data.description, data.errors));
        this.#setStatus(message, 'error', true);
        return;
      }

      const itemCount = selections.length;
      const successTemplate = this.dataset.successTemplate || '[[count]]';
      this.#setStatus(successTemplate.replace('[[count]]', String(itemCount)), 'success', true);
      this.dispatchEvent(
        new CartAddEvent(undefined, this.id, {
          source: 'product-form-component',
          itemCount,
          sections: data.sections,
        })
      );
    } catch (error) {
      const message = this.dataset.errorFallback || '';
      console.error('Unable to add the One & Done rotation.', error);
      this.dispatchEvent(new CartErrorEvent(this.id, message, message, {}));
      this.#setStatus(message, 'error', true);
    } finally {
      this.#setLoading(false);
      this.#updateSummary();
    }
  };

  #showValidationError() {
    const firstInvalidRow = this.#rows.find((row) => !this.#rowState.has(row));
    const invalidControl = firstInvalidRow?.querySelector('select');
    invalidControl?.setAttribute('aria-invalid', 'true');
    this.#setStatus(this.dataset.invalidSelection || '', 'error', true);
  }

  #updateSummary() {
    const selections = this.#rows.map((row) => this.#rowState.get(row)).filter(Boolean);
    const itemCount = selections.length;
    const total = selections.reduce((sum, selection) => sum + selection.variant.price, 0);
    const itemCountElement = this.querySelector('[data-rotation-item-count]');
    const totalElement = this.querySelector('[data-rotation-total]');

    if (itemCountElement) {
      const unit = itemCount === 1 ? this.dataset.itemOne : this.dataset.itemOther;
      itemCountElement.textContent = `${itemCount} ${unit || ''}`.trim();
    }
    if (totalElement) totalElement.textContent = this.#formatPrice(total);
    if (this.#submitButton && this.dataset.state !== 'loading') {
      this.#submitButton.disabled = itemCount !== this.#rows.length;
    }
  }

  /** @param {boolean} isLoading */
  #setLoading(isLoading) {
    this.dataset.state = isLoading ? 'loading' : this.dataset.state === 'loading' ? 'idle' : this.dataset.state || 'idle';

    for (const control of this.querySelectorAll('select, button')) {
      if (!(control instanceof HTMLSelectElement || control instanceof HTMLButtonElement)) continue;
      if (isLoading) {
        control.dataset.rotationWasDisabled = String(control.disabled);
        control.disabled = true;
      } else if (control.dataset.rotationWasDisabled !== undefined) {
        control.disabled = control.dataset.rotationWasDisabled === 'true';
        delete control.dataset.rotationWasDisabled;
      }
    }

    this.#form?.setAttribute('aria-busy', String(isLoading));
    if (this.#buttonLabel) {
      this.#buttonLabel.textContent = isLoading ? this.dataset.addingLabel || '' : this.dataset.addLabel || '';
    }
  }

  /**
   * @param {string} message
   * @param {'idle' | 'loading' | 'success' | 'error'} state
   * @param {boolean} [moveFocus]
   */
  #setStatus(message, state, moveFocus = false) {
    if (!this.#status) return;
    this.dataset.state = state;
    this.#status.textContent = message;
    this.#status.hidden = message === '';
    this.#status.setAttribute('role', state === 'error' ? 'alert' : 'status');
    this.#status.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
    if (moveFocus && message) this.#status.focus({ preventScroll: true });
  }

  #clearStatus() {
    this.#setStatus('', 'idle');
    if (this.#buttonLabel) this.#buttonLabel.textContent = this.dataset.addLabel || '';
  }

  /**
   * @param {RotationProduct} product
   * @param {string[]} selectedValues
   * @returns {RotationVariant | undefined}
   */
  #findExactVariant(product, selectedValues) {
    return product.variants.find(
      (variant) =>
        variant.available && variant.options.every((value, index) => value === selectedValues[index])
    );
  }

  /** @param {HTMLElement} row */
  #getOptionSelects(row) {
    return /** @type {HTMLSelectElement[]} */ (Array.from(row.querySelectorAll('[data-rotation-option]')));
  }

  /** @param {RotationProduct | null | undefined} product */
  #productIsAvailable(product) {
    return Boolean(product?.variants.some((variant) => variant.available));
  }

  /** @param {number} price */
  #formatPrice(price) {
    const currency = this.dataset.currency || 'USD';
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    return formatMoney(price, moneyFormat, currency);
  }
}

if (!customElements.get('ss-one-done-rotation')) {
  customElements.define('ss-one-done-rotation', SsOneDoneRotation);
}

/**
 * @typedef {{
 *   id: number,
 *   title: string,
 *   url: string,
 *   options: string[],
 *   variants: RotationVariant[]
 * }} RotationProduct
 */

/**
 * @typedef {{
 *   id: number,
 *   available: boolean,
 *   price: number,
 *   options: string[]
 * }} RotationVariant
 */
